import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billsService, contractsService, invoiceHiresService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

interface HeaderNotification {
  id: string;
  title: string;
  detail: string;
  path: string;
  color: string;
}

const daysUntil = (date: string) =>
  Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

const HeaderNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<HeaderNotification[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Only query collections the user can actually read
      const empty = Promise.resolve({ data: [] as any[] });
      const [bills, contracts, invoiceHires] = await Promise.allSettled([
        hasPermission('bills') ? billsService.getAll({ pagination: { pageSize: 200 } }) : empty,
        hasPermission('contracts') ? contractsService.getAll({ pagination: { pageSize: 200 } }) : empty,
        hasPermission('invoices') ? invoiceHiresService.getAll({ pagination: { pageSize: 200 } }) : empty,
      ]);
      if (cancelled) return;

      const items: HeaderNotification[] = [];
      const today = new Date().toISOString().split('T')[0];

      if (bills.status === 'fulfilled') {
        (bills.value?.data || []).forEach((bill: any) => {
          const overdue = bill.status === 'overdue' || (bill.status === 'pending' && bill.dueDate && bill.dueDate < today);
          if (overdue) {
            items.push({
              id: `bill-${bill.documentId || bill.id}`,
              title: 'Bill overdue',
              detail: `${bill.category || 'Bill'} — ${Number(bill.amount || 0).toLocaleString()} due ${new Date(bill.dueDate).toLocaleDateString()}`,
              path: '/bills',
              color: 'bg-red-500',
            });
          }
        });
      }

      if (invoiceHires.status === 'fulfilled') {
        (invoiceHires.value?.data || []).forEach((invoice: any) => {
          if (invoice.status === 'overdue') {
            items.push({
              id: `ih-${invoice.documentId || invoice.id}`,
              title: 'Invoice overdue',
              detail: `${invoice.invoiceNumber} — ${Number(invoice.netTotal || 0).toLocaleString()}`,
              path: '/invoice-hire',
              color: 'bg-orange-500',
            });
          }
        });
      }

      if (contracts.status === 'fulfilled') {
        (contracts.value?.data || []).forEach((contract: any) => {
          const status = contract.contract_status || contract.status;
          if (status === 'active' && contract.endDate) {
            const days = daysUntil(contract.endDate);
            if (days >= 0 && days <= 30) {
              items.push({
                id: `contract-${contract.documentId || contract.id}`,
                title: 'Contract expiring soon',
                detail: `${contract.title} — ends in ${days} day${days === 1 ? '' : 's'}`,
                path: '/contracts',
                color: 'bg-yellow-500',
              });
            }
          }
        });
      }

      setNotifications(items);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const count = useMemo(() => notifications.length, [notifications]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative text-gray-500 hover:text-gray-700"
        aria-label="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <span className="text-xs text-gray-500">{count} item{count === 1 ? '' : 's'}</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {count === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications</p>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => { setIsOpen(false); navigate(notification.path); }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${notification.color}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                      <p className="text-xs text-gray-500 truncate">{notification.detail}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderNotifications;
